import { Refresh } from '@mui/icons-material';
import { useState, useEffect } from 'react';

import { BulkPaymentValidation, BulkPaymentResult } from '@/types/payment';
import type { BulkPaymentResponse } from '@/types/transactions';

interface BulkProcessingProps {
  validation: BulkPaymentValidation;
  backendResponse?: BulkPaymentResponse; // Backend response from API
  onProcessingComplete: (results: BulkPaymentResult) => void;
}

export const BulkProcessing: React.FC<BulkProcessingProps> = ({
  validation,
  backendResponse,
  onProcessingComplete,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const validPayments = validation.payments.filter(p => p.isValid);

  useEffect(() => {
    if (isProcessing) return;

    const processPayments = async () => {
      setIsProcessing(true);

      // If we have backend response, transform it to frontend format
      if (backendResponse) {
        // Simulate progress animation
        for (let i = 0; i < validPayments.length; i++) {
          setCurrentIndex(i + 1);
          await new Promise(resolve => setTimeout(resolve, 300));
        }

        // Map backend response to frontend format
        const results: BulkPaymentResult['payments'] = [];

        // Add successful payments (matched by index, then by amount as fallback)
        backendResponse.results.successful.forEach((result, index) => {
          // Try to find by index first, then by amount
          const payment =
            validPayments[index] || validPayments.find(p => p.monto === result.amount);
          if (payment) {
            results.push({
              beneficiario: payment.beneficiario,
              monto: result.amount,
              estado: 'exitoso',
              referencia: result.transactionId,
            });
          }
        });

        // Add failed payments
        backendResponse.results.failed.forEach((result, index) => {
          // Try to find by index first, then by amount
          const offset = backendResponse.results.successful.length;
          const payment =
            validPayments[offset + index] || validPayments.find(p => p.monto === result.amount);
          if (payment) {
            results.push({
              beneficiario: payment.beneficiario,
              monto: result.amount,
              estado: 'fallido',
              // Only show error message as reference, not fake transaction IDs
              referencia: result.error,
            });
          }
        });

        // Add scheduled payments
        backendResponse.results.scheduled.forEach((result, index) => {
          // Match by amount and scheduled date
          const payment =
            validPayments.find(
              p =>
                p.monto === result.amount &&
                (!p.fecha || p.fecha === result.scheduledDate.split('T')[0])
            ) || validPayments[index];

          if (payment) {
            results.push({
              beneficiario: payment.beneficiario,
              monto: result.amount,
              estado: 'programado',
              scheduledDate: result.scheduledDate,
              referencia: result.scheduledTransactionId,
            });
          }
        });

        const finalResults: BulkPaymentResult = {
          successful: backendResponse.summary.successful,
          failed: backendResponse.summary.failed,
          scheduled: backendResponse.summary.scheduled,
          payments: results,
        };

        setTimeout(() => {
          onProcessingComplete(finalResults);
        }, 500);
      } else {
        // Fallback: No backend response (shouldn't happen in production)
        console.warn('No se recibió respuesta del servidor en BulkProcessing');
        const results: BulkPaymentResult['payments'] = validPayments.map(payment => ({
          beneficiario: payment.beneficiario,
          monto: payment.monto,
          estado: 'fallido' as const,
        }));

        const finalResults: BulkPaymentResult = {
          successful: 0,
          failed: validPayments.length,
          scheduled: 0,
          payments: results,
        };

        setTimeout(() => {
          onProcessingComplete(finalResults);
        }, 500);
      }
    };

    void processPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to run only once

  const progressPercentage =
    validPayments.length > 0 ? Math.round((currentIndex / validPayments.length) * 100) : 0;

  return (
    <div className="max-w-2xl mx-auto text-center">
      {/* Processing Icon */}
      <div className="w-24 h-24 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
        <Refresh className="text-blue-400 animate-spin" style={{ fontSize: '3rem' }} />
      </div>

      {/* Processing Status */}
      <h2 className="text-3xl font-bold text-brand-white mb-2">Procesando Lote...</h2>
      <p className="text-muted mb-8">Por favor espera mientras procesamos todos los pagos</p>

      {/* Progress Bar */}
      <div className="w-full bg-black/10 dark:bg-white/10 rounded-full h-4 mb-4">
        <div
          className="bg-brand-blue h-4 rounded-full transition-all duration-1000"
          style={{ width: `${progressPercentage}%` }}
        />
      </div>

      <div className="text-sm text-muted mb-8">
        {currentIndex} de {validPayments.length} pagos procesados
      </div>

      {/* Current Processing Status */}
      {currentIndex <= validPayments.length && (
        <div className="text-center">
          <div className="text-lg font-medium text-brand-white mb-2">
            Procesando: {validPayments[currentIndex - 1]?.beneficiario || 'Iniciando...'}
          </div>
          <div className="text-sm text-muted">
            {validPayments[currentIndex - 1]?.monto
              ? new Intl.NumberFormat('es-MX', {
                  style: 'currency',
                  currency: 'MXN',
                }).format(validPayments[currentIndex - 1]?.monto || 0)
              : ''}
          </div>
        </div>
      )}
    </div>
  );
};
